import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Stars } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import * as THREE from 'three';
import { Disaster } from '../../data/mockData';

interface GlobeComponentProps {
  disasters: Disaster[];
}

// Earth component with rotation and programmatic texture
const Earth: React.FC<{ disasters: Disaster[] }> = ({ disasters }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const { camera } = useThree();
  
  // Create programmatic Earth texture
  const earthTexture = React.useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const context = canvas.getContext('2d')!;
    
    // Create a simple Earth-like texture
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#87CEEB'); // Sky blue
    gradient.addColorStop(0.3, '#228B22'); // Forest green
    gradient.addColorStop(0.7, '#8B4513'); // Saddle brown
    gradient.addColorStop(1, '#4682B4'); // Steel blue
    
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add some noise for texture
    for (let i = 0; i < 1000; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const size = Math.random() * 3;
      context.fillStyle = `rgba(${Math.random() * 100}, ${Math.random() * 100 + 100}, ${Math.random() * 100 + 50}, 0.3)`;
      context.fillRect(x, y, size, size);
    }
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    return texture;
  }, []);

  // Auto-rotate the earth
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.1;
    }
  });

  // Convert lat/lng to 3D coordinates on sphere
  const latLngToVector3 = (lat: number, lng: number, radius: number = 2.02) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lng + 180) * (Math.PI / 180);
    
    return new THREE.Vector3(
      -radius * Math.sin(phi) * Math.cos(theta),
      radius * Math.cos(phi),
      radius * Math.sin(phi) * Math.sin(theta)
    );
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  return (
    <group>
      {/* Earth sphere */}
      <mesh ref={meshRef} position={[0, 0, 0]}>
        <sphereGeometry args={[2, 64, 64]} />
        <meshStandardMaterial 
          map={earthTexture}
          roughness={0.8}
          metalness={0.1}
        />
      </mesh>

      {/* Disaster markers */}
      {disasters.map((disaster) => {
        const position = latLngToVector3(disaster.coordinates[1], disaster.coordinates[0]);
        const color = getSeverityColor(disaster.severity);
        
        return (
          <group key={disaster.id}>
            {/* Marker sphere */}
            <mesh position={position}>
              <sphereGeometry args={[0.03, 8, 8]} />
              <meshBasicMaterial color={color} />
            </mesh>
            
            {/* Pulsing ring for active disasters */}
            {disaster.status === 'active' && (
              <mesh position={position} rotation={[Math.PI / 2, 0, 0]}>
                <ringGeometry args={[0.05, 0.08, 16]} />
                <meshBasicMaterial 
                  color={color} 
                  transparent 
                  opacity={0.6}
                  side={THREE.DoubleSide}
                />
              </mesh>
            )}
          </group>
        );
      })}
    </group>
  );
};

// Main Globe Component
const GlobeComponent: React.FC<GlobeComponentProps> = ({ disasters }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedDisaster, setSelectedDisaster] = useState<Disaster | null>(null);
  const controlsRef = useRef<any>(null);

  // Handle ESC key for fullscreen exit
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFullscreen]);

  const handleGlobeClick = () => {
    if (!isFullscreen) {
      setIsFullscreen(true);
    }
  };

  const resetCamera = () => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  };

  return (
    <>
      {/* Dashboard Globe Container */}
      <motion.div
        layout
        className={`relative overflow-hidden rounded-xl border border-dark-700 bg-dark-900 cursor-pointer ${
          isFullscreen ? 'fixed inset-0 z-50' : 'h-96'
        }`}
        onClick={!isFullscreen ? handleGlobeClick : undefined}
        initial={false}
        animate={{
          scale: isFullscreen ? 1 : 1,
        }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      >
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-accent-green rounded-full animate-pulse"></div>
            <span className="text-white font-semibold">
              {isFullscreen ? 'Interactive Earth Globe' : 'Global Disaster Map'}
            </span>
          </div>
          
          {isFullscreen && (
            <div className="flex items-center space-x-2">
              <button
                onClick={resetCamera}
                className="p-2 bg-dark-800/80 backdrop-blur-sm rounded-lg hover:bg-dark-700 transition-colors"
              >
                <RotateCcw className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 bg-dark-800/80 backdrop-blur-sm rounded-lg hover:bg-dark-700 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* 3D Canvas */}
        <Canvas
          camera={{ 
            position: [0, 0, 5], 
            fov: 50,
            near: 0.1,
            far: 1000
          }}
          style={{ 
            width: '100%', 
            height: '100%',
            background: 'transparent'
          }}
        >
          {/* Lighting */}
          <ambientLight intensity={0.4} />
          <directionalLight 
            position={[5, 5, 5]} 
            intensity={1}
            castShadow
          />
          <pointLight position={[-5, -5, -5]} intensity={0.5} />

          {/* Stars background */}
          <Stars 
            radius={300} 
            depth={60} 
            count={2000} 
            factor={7} 
            saturation={0} 
            fade 
          />

          {/* Earth */}
          <Earth disasters={disasters} />

          {/* Controls */}
          <OrbitControls
            ref={controlsRef}
            enablePan={isFullscreen}
            enableZoom={true}
            enableRotate={true}
            minDistance={isFullscreen ? 2.5 : 4}
            maxDistance={isFullscreen ? 15 : 8}
            autoRotate={!isFullscreen}
            autoRotateSpeed={0.5}
          />
        </Canvas>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-dark-900/90 backdrop-blur-sm border border-dark-700 rounded-lg p-3">
          <h4 className="text-sm font-semibold mb-2 text-white">Disaster Severity</h4>
          <div className="space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-accent-red"></div>
              <span className="text-gray-300">Critical</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-accent-orange"></div>
              <span className="text-gray-300">High</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-accent-yellow"></div>
              <span className="text-gray-300">Medium</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-accent-green"></div>
              <span className="text-gray-300">Low</span>
            </div>
          </div>
        </div>

        {/* Instructions */}
        {!isFullscreen && (
          <div className="absolute bottom-4 right-4 bg-dark-900/90 backdrop-blur-sm border border-dark-700 rounded-lg p-3">
            <p className="text-xs text-gray-300">Click to expand</p>
          </div>
        )}

        {isFullscreen && (
          <div className="absolute bottom-4 right-4 bg-dark-900/90 backdrop-blur-sm border border-dark-700 rounded-lg p-3">
            <div className="text-xs text-gray-300 space-y-1">
              <p>• Drag to rotate</p>
              <p>• Scroll to zoom</p>
              <p>• ESC to exit</p>
            </div>
          </div>
        )}

        {/* Disaster count indicator */}
        <div className="absolute top-4 right-4 bg-dark-900/90 backdrop-blur-sm border border-dark-700 rounded-lg px-3 py-2">
          <div className="text-sm font-semibold text-white">
            {disasters.filter(d => d.status === 'active').length} Active
          </div>
          <div className="text-xs text-gray-400">
            {disasters.length} Total
          </div>
        </div>
      </motion.div>

      {/* Fullscreen overlay backdrop */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={() => setIsFullscreen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobeComponent;